// src/lib/dictee.js — LA DICTÉE, ISOLÉE ET PURE-TESTABLE.
//
// Le panneau du micro a un contrat ÉTROIT, dessiné par la maquette :
// « il écoute, récite ce qu'il a compris, emmène » — et explicitement PAS :
// pas de proposition affichée, pas d'envoi. Ce module ne porte donc QUE
// l'écoute et la restitution ; il ne sait ni proposer ni envoyer, par
// construction — on ne peut pas mal s'en servir.
//
// ⚠️ IL NE MEUBLE PAS (règle du micro lui-même) : quand la reconnaissance
// vocale n'est pas disponible, on le DIT. Pas de fausse animation, pas de
// « bientôt ». Firefox n'implémente pas l'API — c'est un cas courant, pas un
// bug à masquer.

/** L'API du navigateur, ou null. Jamais d'exception à l'import. */
function apiDictee() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/** Raisons d'indisponibilité — chacune a sa phrase, aucune n'est muette. */
const RAISONS = Object.freeze({
  absente: "Votre navigateur ne sait pas écouter (Firefox, notamment). Ouvrez la destination directement.",
  refus: "Le micro est refusé pour ce site. Autorisez-le dans les réglages du navigateur.",
  panne: "L'écoute s'est interrompue. Réessayez, ou ouvrez la destination directement.",
  vide: "Je n'ai rien entendu.",
});

function estDisponible() {
  return apiDictee() !== null;
}

/**
 * Démarre une écoute. Rend un objet { arreter() }.
 * @param {object} h — { onTexte(texte, definitif), onFin(), onErreur(raison) }
 */
function ecouter(h = {}) {
  const Api = apiDictee();
  if (!Api) {
    if (h.onErreur) h.onErreur('absente');
    return { arreter() {} };
  }
  let reco;
  try {
    reco = new Api();
  } catch (_) {
    if (h.onErreur) h.onErreur('panne');
    return { arreter() {} };
  }
  reco.lang = 'fr-FR';
  reco.continuous = false;
  reco.interimResults = true;

  reco.onresult = (ev) => {
    let texte = '';
    let definitif = false;
    for (let i = 0; i < ev.results.length; i += 1) {
      texte += ev.results[i][0].transcript;
      if (ev.results[i].isFinal) definitif = true;
    }
    if (h.onTexte) h.onTexte(texte.trim(), definitif);
  };
  reco.onerror = (ev) => {
    const code = ev && ev.error;
    // 'not-allowed' / 'service-not-allowed' = l'utilisateur (ou la politique
    // du site) a refusé le micro. Tout le reste est une panne.
    const raison = code === 'not-allowed' || code === 'service-not-allowed' ? 'refus' : 'panne';
    if (h.onErreur) h.onErreur(raison);
  };
  reco.onend = () => { if (h.onFin) h.onFin(); };

  try {
    reco.start();
  } catch (_) {
    if (h.onErreur) h.onErreur('panne');
  }
  return {
    arreter() { try { reco.stop(); } catch (_) { /* déjà arrêtée */ } },
  };
}

module.exports = { estDisponible, ecouter, RAISONS, _apiDictee: apiDictee };
